function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export interface CAPIEventOptions {
  event_name: string;
  event_id: string;
  event_source_url: string;
  user_data?: {
    em?: string;
    ph?: string;
    fn?: string;
    ln?: string;
    external_id?: string;
    fbc?: string;
    fbp?: string;
  };
  custom_data?: Record<string, unknown>;
}

export type CAPISendResult =
  | { ok: true; status: number }
  | {
      ok: false;
      status?: number;
      reason: "missing_endpoint" | "network_error" | "invalid_response" | "upstream_rejected" | "no_event_accepted";
    };

function readEventsReceived(responseBody: unknown): number | undefined {
  if (typeof responseBody !== "object" || responseBody === null) return undefined;

  const wrappedData = "data" in responseBody ? responseBody.data : undefined;
  const payload = typeof wrappedData === "object" && wrappedData !== null ? wrappedData : responseBody;
  const eventsReceived =
    "eventsReceived" in payload ? payload.eventsReceived : "events_received" in payload ? payload.events_received : undefined;
  return typeof eventsReceived === "number" ? eventsReceived : undefined;
}

export async function sendCAPIEvent(options: CAPIEventOptions): Promise<CAPISendResult> {
  const { event_name, event_id, event_source_url, user_data = {}, custom_data } = options;
  const apiHostname = process.env.NEXT_PUBLIC_HOSTNAME?.replace(/\/$/, "");
  if (!apiHostname) return { ok: false, reason: "missing_endpoint" };

  const fbc = user_data.fbc ?? getCookie("_fbc");
  const fbp = user_data.fbp ?? getCookie("_fbp");

  try {
    const response = await fetch(`${apiHostname}/meta/capi/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name,
        event_id,
        event_source_url,
        user_data: { ...user_data, fbc, fbp },
        custom_data,
      }),
    });
    let responseBody: unknown;
    try {
      responseBody = await response.json();
    } catch {
      return { ok: false, status: response.status, reason: "invalid_response" };
    }

    if (!response.ok) return { ok: false, status: response.status, reason: "upstream_rejected" };
    const eventsReceived = readEventsReceived(responseBody);
    if (eventsReceived === undefined || eventsReceived < 1) {
      return { ok: false, status: response.status, reason: "no_event_accepted" };
    }

    return { ok: true, status: response.status };
  } catch {
    return { ok: false, reason: "network_error" };
  }
}
